import React, { useState } from 'react';
import { getVideoMeta } from '../utils/videoUtils';

/**
 * Reusable video embed component.
 *
 * Props:
 *   url       — raw video URL
 *   meta      — pre-computed { platform, video_id, embed_url, thumbnail_url } from API (optional)
 *   compact   — if true, shows thumbnail with play overlay; click expands to iframe
 */
export default function VideoEmbed({ url, meta, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [thumbError, setThumbError] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const videoMeta = meta || getVideoMeta(url);
  if (!videoMeta || !url) return null;

  // Uploaded video file in our S3 bucket — play inline, with a server-extracted
  // poster. If the browser can't decode the codec (e.g. HEVC in Chrome), the
  // <video> errors and we fall through to the poster-thumbnail link below —
  // keeping the thumbnail visible rather than dropping to a bare button.
  if (videoMeta.platform === 'file' && !videoError) {
    return (
      <div className="video-embed">
        <video
          className="video-embed__iframe"
          src={`${url}#t=0.1`}
          poster={videoMeta.thumbnail_url || undefined}
          controls
          preload="metadata"
          playsInline
          onError={() => setVideoError(true)}
        />
      </div>
    );
  }

  // YouTube embed
  if (videoMeta.platform === 'youtube' && videoMeta.embed_url) {
    // Compact mode: thumbnail + play overlay, click to expand
    if (compact && !expanded) {
      const thumbSrc = thumbError
        ? `https://img.youtube.com/vi/${videoMeta.video_id}/default.jpg`
        : videoMeta.thumbnail_url;

      return (
        <div
          className="video-embed__thumbnail"
          onClick={() => setExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(true); } }}
          aria-label="Play video"
        >
          <img
            src={thumbSrc}
            alt="Video thumbnail"
            className="video-embed__thumbnail-img"
            onError={() => { if (!thumbError) setThumbError(true); }}
          />
          <div className="video-embed__play-btn" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 68 48">
              <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/>
              <path d="M45 24L27 14v20" fill="white"/>
            </svg>
          </div>
        </div>
      );
    }

    // Full embed (or expanded from compact)
    return (
      <div className="video-embed">
        <iframe
          className="video-embed__iframe"
          src={videoMeta.embed_url}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // Non-YouTube with a scrapable thumbnail (e.g. Hudl, proxied same-origin):
  // show the thumbnail + play overlay, linking out to the source in a new tab.
  // Falls back to the styled external link below on image error or no thumbnail.
  if (videoMeta.thumbnail_url && !thumbError) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="video-embed__thumbnail"
        aria-label="Watch video"
      >
        <img
          src={videoMeta.thumbnail_url}
          alt="Video thumbnail"
          className="video-embed__thumbnail-img"
          onError={() => setThumbError(true)}
        />
        <div className="video-embed__play-btn" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 68 48">
            <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="rgba(0,0,0,0.65)"/>
            <path d="M45 24L27 14v20" fill="white"/>
          </svg>
        </div>
      </a>
    );
  }

  // Non-YouTube fallback: styled external link
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="video-embed__external-link"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
      <span>
        {videoMeta.platform === 'hudl' ? 'Watch on Hudl' : 'Watch Video'}
      </span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
    </a>
  );
}
